/**
 * @file vehicleLoad.js
 * @version 1.14.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-06-18
 * @modified 2026-10-07
 * @description Pure load-analysis helpers for Transport Planning.
 *
 *   A vehicle does one or more LOADS in a day: set off, deliver a batch of
 *   orders, (maybe) return to collect more, etc. A "load" is one trip's worth of
 *   drops. segmentLoads() splits a vehicle's rows (in board order) into loads by
 *   collection TIME — a load may collect from several suppliers/mills — or by
 *   RUN when rows were combined into one (then several times are one load).
 *
 *   For a single load, analyzeVehicleLoad() works out payload headroom and
 *   whether the compartments can carry the loads — for both fixed-compartment and
 *   moveable-door bodies — IN TABLE ORDER. The board table is in DELIVERY order:
 *   index 0 = top row = FIRST drop = REAR-most compartment (you unload from the
 *   back), so drops fill rear→front. That order is the user's plan and is NEVER
 *   silently re-sequenced. It returns the orders→compartments assignment, an
 *   efficiency %, and (for moveable bodies) the door layout to use.
 *   enumerateConfigs() lists the moveable-door layouts, reporting for each both
 *   its efficiency IN THE CURRENT SEQUENCE and its best efficiency if the drops
 *   were re-ordered (which the modal offers, but only the user may accept).
 *
 *   1.14.0 (2026-10-07): loadDistributionOk, validateRangeDoors and
 *   efficiencyOf are exported for the Loads and Drops day engine
 *   (utils/dayPlan.js), which plans a lorry's whole day and lays out each
 *   load around what is still on board. Nothing here changed behaviour.
 *   The server holds a generated copy (utils/vehicleLoad.js in the parent
 *   repo, scripts/sync-shared-utils.js) — edit this file, then re-run it.
 */

const EPS = 1e-6;
const round = (n) => Math.round(n * 1000) / 1000;
const sum = (arr) => arr.reduce((s, v) => s + (Number.isFinite(v) ? v : 0), 0);

// Usable payload in tonnes (a fitted Moffett costs ~1t). null if unknown.
export function usablePayload(vehicle) {
  const p = Number(vehicle?.payload);
  if (!Number.isFinite(p)) return null;
  return round(Math.max(0, p - (vehicle?.has_moffett ? 1 : 0)));
}

// Overall severity for a load analysis: 'danger' | 'warn' | 'ok' | 'muted'.
// Hard problems (over payload / won't fit / too many drops) are always danger;
// otherwise the EFFICIENCY drives it — < 40% danger, < 60% warn, else ok (but a
// load that needs re-sequencing stays at least warn).
export function loadSeverity(load) {
  if (!load) return 'muted';
  if (load.overPayload || load.fit === 'too-many' || load.fit === 'wont-fit') return 'danger';
  if (load.efficiency == null) return 'muted';
  if (load.efficiency < 40) return 'danger';
  if (load.efficiency < 60) return 'warn';
  if (load.fit === 'reorder') return 'warn';
  return 'ok';
}

// Map a severity to a CSS colour var.
export const SEVERITY_COLOR = {
  danger: 'var(--color-error)',
  warn: 'var(--color-warning)',
  ok: 'var(--color-success)',
  muted: 'var(--text-muted)'
};

// FIT severity (drives the card's left border + the Fits/Won't-fit badge) —
// purely about whether the load physically fits, independent of efficiency.
export function fitSeverity(load) {
  if (!load) return 'muted';
  if (load.overPayload || load.fit === 'too-many' || load.fit === 'wont-fit') return 'danger';
  if (load.fit === 'reorder') return 'warn';
  if (load.fit === 'no-spec') return 'muted';
  return 'ok';
}

// EFFICIENCY severity (drives the ring + payload bar) — < 40% danger, < 60% warn.
export function efficiencySeverity(load) {
  if (!load || load.efficiency == null) return 'muted';
  if (load.efficiency < 40) return 'danger';
  if (load.efficiency < 60) return 'warn';
  return 'ok';
}

// ── Moveable-door RANGE model ────────────────────────────────────
// A moveable body is defined by a fixed compartment count and a min–max tonnage
// RANGE per compartment (front→back, 1T increments). The doors partition the
// whole body, so the chosen compartment SIZES always sum to the usable payload —
// that is a purely geometric choice of where the bulkheads sit.
//
// The rear-lift flag imposes a weight-DISTRIBUTION rule on the actual LOADED
// weight, but ONLY when the vehicle is loaded to its max usable payload (a
// part-loaded vehicle stays well within its axle limits, so any arrangement is
// fine). At max payload:
//   - rear_lift ON  → most of the loaded weight must sit over the FRONT half
//   - rear_lift OFF → most of the loaded weight must sit over the REAR half
// A perfectly balanced (symmetrical) load always passes. The half boundary is by
// compartment COUNT: front half = ceil(n/2) compartments (front-biased for odd
// counts). The front/rear-half CAPS are not enforced.

// Front-half compartment count (front-biased for odd counts).
const frontHalfCount = (n) => Math.ceil(n / 2);

// Check a packed assignment (REAR→FRONT, per-compartment loaded portions) against
// the vehicle's rear-lift weight-distribution rule. Only applies when the vehicle
// is at its max usable payload; part-loaded vehicles always pass. Returns true
// when the load is legal.
export function loadDistributionOk(assignment, vehicle) {
  if (!assignment || !assignment.length) return true;
  const eff = usablePayload(vehicle);
  const total = round(sum(assignment.map(a => a?.portion || 0)));
  // Distribution only matters at max payload — a part-loaded vehicle is fine
  // however it's arranged.
  if (eff == null || total < eff - EPS) return true;
  const n = assignment.length;
  const fCount = frontHalfCount(n);
  // assignment is rear→front, so the FRONT half is the tail of the array.
  const frontLoaded = round(sum(assignment.slice(n - fCount).map(a => a?.portion || 0)));
  const rearLoaded = round(sum(assignment.slice(0, n - fCount).map(a => a?.portion || 0)));
  const rearLift = !!vehicle?.rear_lift;
  // "Most of the weight" over the required half (balanced is allowed).
  if (rearLift ? (frontLoaded < rearLoaded - EPS) : (rearLoaded < frontLoaded - EPS)) return false;
  return true;
}

// Human-readable reason a full load can't be laid out in the CURRENT drop order
// under the rear-lift weight rule (only reached at max payload). The fix is the
// user's to make — re-order the rows, or accept a layout that needs a re-sequence.
function distributionMessage(vehicle) {
  const half = vehicle?.rear_lift ? 'front (rear lift fitted)' : 'rear';
  return `At full payload this drop order can't keep most of the weight over the ${half} half. Re-order the drops, or pick a layout below that needs a sequence change.`;
}

// Enumerate every valid front→back integer size configuration for a moveable
// body from its ranges alone (sizes sum to round(cap)). Axle rules are NOT
// applied here — they depend on the load, not the compartment sizes.
function enumerateRangeConfigs(vehicle, cap, limit = 500) {
  const ranges = Array.isArray(vehicle?.compartment_ranges) ? vehicle.compartment_ranges : [];
  const n = ranges.length;
  const target = Math.round(cap);
  if (!n || !Number.isFinite(target) || target <= 0) return [];

  const mins = ranges.map(r => Math.max(0, Math.floor(Number(r?.min) || 0)));
  const maxs = ranges.map((r, i) => {
    const mx = Math.floor(Number(r?.max));
    return Number.isFinite(mx) ? Math.max(mx, mins[i]) : target; // blank max → up to payload
  });

  // Suffix min/max sums for feasibility pruning.
  const sufMin = new Array(n + 1).fill(0);
  const sufMax = new Array(n + 1).fill(0);
  for (let i = n - 1; i >= 0; i--) {
    sufMin[i] = sufMin[i + 1] + mins[i];
    sufMax[i] = sufMax[i + 1] + maxs[i];
  }
  if (sufMin[0] > target || sufMax[0] < target) return []; // ranges can't sum to payload

  const out = [];
  const cur = new Array(n).fill(0);
  const dfs = (i, sumSoFar) => {
    if (out.length >= limit) return;
    if (i === n) {
      if (sumSoFar === target) out.push(cur.slice());
      return;
    }
    const remaining = target - sumSoFar;
    const lo = Math.max(mins[i], remaining - sufMax[i + 1]);
    const hi = Math.min(maxs[i], remaining - sufMin[i + 1]);
    for (let s = lo; s <= hi; s++) {
      cur[i] = s;
      dfs(i + 1, sumSoFar + s);
      if (out.length >= limit) return;
    }
  };
  dfs(0, 0);
  return out;
}

// Turn a front→back size config into { doors (cumulative t from front, count-1),
// sizes (REAR→FRONT, for the packer — index 0 = rear-most compartment, which is
// the TOP row in the board table = the FIRST drop, unloaded from the back). }.
function configToDoorsSizes(front2back) {
  const doors = [];
  let c = 0;
  for (let i = 0; i < front2back.length - 1; i++) { c = round(c + front2back[i]); doors.push(c); }
  return { doors, sizes: front2back.slice().reverse().map(round) };
}

// All moveable layouts for a range-model vehicle: [{ doors, sizes(rear→front) }].
export function moveableRangeConfigs(vehicle, cap) {
  return enumerateRangeConfigs(vehicle, cap).map(configToDoorsSizes);
}

// Front-half / rear-half SIZE totals of a config (sizes REAR→FRONT, so the front
// half is the TAIL of the array). The half boundary is by compartment count
// (front = ceil(n/2) compartments, front-biased for odd counts).
export function configHalfSums(config) {
  const sizes = Array.isArray(config?.sizes) ? config.sizes : [];
  const n = sizes.length;
  const fCount = frontHalfCount(n);
  return {
    frontSum: round(sum(sizes.slice(n - fCount))),
    rearSum: round(sum(sizes.slice(0, n - fCount)))
  };
}

// Pick the layout to PREVIEW for a vehicle in the editor: the one whose front/rear
// half SIZES are as balanced (symmetrical) as possible. When they can't be equal
// (e.g. an odd payload), bias the heavier half to the side the rear-lift rule
// wants — front WITH a rear lift, rear WITHOUT. `configs` come from
// moveableRangeConfigs (sizes rear→front). Returns the chosen config, or null.
export function pickBalancedPreview(vehicle, configs) {
  if (!Array.isArray(configs) || !configs.length) return null;
  const rearLift = !!vehicle?.rear_lift;
  let best = null;
  for (const c of configs) {
    const { frontSum, rearSum } = configHalfSums(c);
    const imbalance = Math.abs(round(frontSum - rearSum));
    const symmetrical = imbalance < EPS;
    // "Correct" = symmetrical, or the heavier half is the one the rule wants.
    const correctDir = symmetrical || (rearLift ? frontSum > rearSum : rearSum > frontSum);
    const cand = { c, imbalance, correctDir };
    if (!best) { best = cand; continue; }
    // Prefer a correct-direction layout, then the smallest front/rear imbalance.
    if (cand.correctDir !== best.correctDir) { if (cand.correctDir) best = cand; continue; }
    if (cand.imbalance < best.imbalance - EPS) best = cand;
  }
  return best ? best.c : null;
}

// Validate a stored/override door set (cumulative t from front) against a
// range-model vehicle: every resulting compartment must sit in its range and the
// sizes must sum to the payload. Axle rules (half caps + rear lift) are load-based
// and checked separately in loadDistributionOk(). Returns sizes rear→front if the
// geometry is valid, else null.
export function validateRangeDoors(overrideDoors, vehicle, cap) {
  const ranges = Array.isArray(vehicle?.compartment_ranges) ? vehicle.compartment_ranges : [];
  const n = ranges.length;
  if (n < 1 || !Array.isArray(overrideDoors) || overrideDoors.length !== n - 1) return null;
  const doors = overrideDoors.map(Number).filter(Number.isFinite).sort((a, b) => a - b);
  if (doors.length !== n - 1) return null;
  const target = Math.round(cap);
  const bounds = [0, ...doors, target];
  const f2b = [];
  for (let i = 1; i < bounds.length; i++) f2b.push(round(bounds[i] - bounds[i - 1]));
  if (Math.abs(sum(f2b) - target) > EPS) return null;
  for (let i = 0; i < n; i++) {
    const mn = Math.max(0, Number(ranges[i]?.min) || 0);
    const mxRaw = Number(ranges[i]?.max);
    const mx = Number.isFinite(mxRaw) ? mxRaw : target;
    if (f2b[i] < mn - EPS || f2b[i] > mx + EPS) return null;
  }
  return f2b.slice().reverse();
}

/**
 * Split a vehicle's rows (already in board/drop order) into LOADS. A new load
 * starts when the collection TIME changes (a driver setting off again later in
 * the day is a separate load). Suppliers/mills are NOT a split boundary — one
 * load can collect from several. Null/blank times never force a split (they join
 * the current load and adopt its time).
 *
 * A combined RUN (rows sharing a run_key — "Combine into one run") is one load
 * whatever its rows' times: a driver can collect at 06:00 at one mill and 07:00
 * at the next en route, then deliver the lot. A run's load takes its EARLIEST
 * time, and `times` lists every collection time in it.
 * @param {object[]} rows rows with .scheduled_time (and optionally .run_key)
 * @returns {{time:string|null, times:string[], run:string|null, rows:object[]}[]}
 */
export function segmentLoads(rows) {
  const loads = [];
  let current = null;
  for (const row of rows) {
    const time = row.scheduled_time || null;
    const run = row.run_key || null;
    let sameLoad;
    if (!current) sameLoad = false;
    else if (run) sameLoad = current.run === run;          // a run only joins itself
    else if (current.run) sameLoad = time == null;         // an untimed row still joins, as before
    else sameLoad = time == null || current.time == null || current.time === time;
    if (!sameLoad) {
      current = { time, times: [], run, rows: [] };
      loads.push(current);
    } else if (time != null && (current.time == null || (current.run && time < current.time))) {
      current.time = time; // adopt the first concrete time seen (a run: the earliest)
    }
    if (time != null && !current.times.includes(time)) current.times.push(time);
    current.rows.push(row);
  }
  for (const ld of loads) ld.times.sort();
  return loads;
}

// ── Block packing ────────────────────────────────────────────────
// Pack orders into compartments FRONT→REAR (sizes index 0 = front). A single
// order may SPAN multiple contiguous compartments (split, e.g. a 6t order across
// two 3t compartments). Compartments are never shared between different orders
// UNLESS adjacent orders are the SAME PRODUCT and allowMerge is set (then they
// share — flagged "mixed"). Each order/unit is filled greedily
// compartment-by-compartment; empties land at the rear. Returns a per-compartment
// array (length = sizes.length) or null if it doesn't fit.
//   assign[i] = { size, lineIndex|null, lines:[]|null, portion, fill, mixed }
function packUnits(sizes, seq, lineTonnes, productOf, allowMerge) {
  // Group the drop sequence into units (consecutive lines, merged when they are
  // the same product and merging is allowed).
  const units = [];
  for (const li of seq) {
    const prev = units[units.length - 1];
    const sameProduct = prev && productOf(prev.lines[0]) != null && productOf(li) === productOf(prev.lines[0]);
    if (allowMerge && sameProduct) {
      prev.lines.push(li);
      prev.tonnes = round(prev.tonnes + lineTonnes[li]);
    } else {
      units.push({ lines: [li], tonnes: round(lineTonnes[li]) });
    }
  }
  const assign = sizes.map(s => ({ size: round(s), lineIndex: null, lines: null, portion: 0, fill: null, mixed: false }));
  let ci = 0;
  for (const unit of units) {
    if (ci >= sizes.length) return null;
    let rem = unit.tonnes;
    const mixed = unit.lines.length > 1;
    let placed = false;
    while (ci < sizes.length && rem > EPS) {
      const take = Math.min(sizes[ci], rem);
      if (take <= EPS) break; // zero-size compartment can't carry anything
      assign[ci].lineIndex = unit.lines[0];
      assign[ci].lines = unit.lines.slice();
      assign[ci].portion = round(take);
      assign[ci].fill = sizes[ci] > 0 ? Math.min(take / sizes[ci], 1) : null;
      assign[ci].mixed = mixed;
      rem = round(rem - take);
      ci++;
      placed = true;
    }
    if (rem > EPS || !placed) return null; // didn't fit
  }
  return assign;
}

// Total size of the compartments that actually hold something (lower = tighter,
// leaving the empty compartments larger).
const usedSizeSum = (assign) => sum(assign.filter(c => c.portion > EPS).map(c => c.size));

const productKey = (products, li) => (products && products[li] != null)
  ? String(products[li]).trim().toLowerCase() : null;

// Pack a load for a known config (sizes rear→front) + drop sequence. Tries
// without mixing first, then allowing same-product mixing. Used by the modal to
// lay out a previewed configuration. Returns the per-compartment assignment or
// null.
export function packLoad(sizes, seq, lineTonnes, products) {
  const productOf = (li) => productKey(products, li);
  return packUnits(sizes, seq, lineTonnes, productOf, false)
      || packUnits(sizes, seq, lineTonnes, productOf, true);
}

// Candidate drop orders to try: current, then heaviest-first, then lightest-first.
function candidateSeqs(lineTonnes) {
  const idx = lineTonnes.map((_, i) => i);
  const desc = idx.slice().sort((a, b) => lineTonnes[b] - lineTonnes[a]);
  const asc = idx.slice().sort((a, b) => lineTonnes[a] - lineTonnes[b]);
  const seen = new Set();
  return [idx, desc, asc].filter(s => { const k = s.join(','); if (seen.has(k)) return false; seen.add(k); return true; });
}

// Blend payload utilisation (empty space across the whole body) with the average
// per-compartment fill of the USED compartments (an over-sized compartment is
// penalised — e.g. 3t in a 9t compartment = 33%). 0.6 / 0.4 weighting. Returns
// integer percentages.
export function efficiencyOf(assignment, total, eff) {
  const payloadUtil = eff != null && eff > 0 ? Math.min(total / eff, 1) : 0;
  const usedFills = assignment ? assignment.filter(a => a.portion > EPS && a.fill != null).map(a => a.fill) : null;
  const compartmentFill = (usedFills && usedFills.length)
    ? usedFills.reduce((s, f) => s + f, 0) / usedFills.length
    : payloadUtil;
  let efficiency = null;
  if (eff != null) {
    efficiency = Math.round(100 * ((usedFills && usedFills.length) ? (0.6 * payloadUtil + 0.4 * compartmentFill) : payloadUtil));
  } else if (usedFills && usedFills.length) {
    efficiency = Math.round(100 * compartmentFill);
  }
  return {
    payloadUtil: Math.round(payloadUtil * 100),
    compartmentFill: Math.round(compartmentFill * 100),
    efficiency
  };
}

/**
 * Analyse a single LOAD.
 * @param {object} vehicle full vehicle spec
 * @param {number[]} lineTonnes per-line tonnage, index 0 = top table row (front)
 * @param {(string|null)[]} [products] per-line product key (enables same-product mixing)
 * @param {number[]|null} [overrideDoors] a user-chosen moveable-door layout to
 *        honour instead of the auto-picked tightest one (cumulative tonnes from
 *        front). Ignored if it no longer matches the vehicle's door slots.
 */
export function analyzeVehicleLoad(vehicle, lineTonnes, products, overrideDoors) {
  const eff = usablePayload(vehicle);
  const total = round(sum(lineTonnes));
  const overPayload = eff != null && total > eff + EPS;
  const overBy = overPayload ? round(total - eff) : 0;

  const n = lineTonnes.length;
  const moveable = !!vehicle?.has_moveable_doors;
  const rangesArr = Array.isArray(vehicle?.compartment_ranges) ? vehicle.compartment_ranges : [];
  const compartmentCount = (moveable && rangesArr.length)
    ? rangesArr.length
    : (vehicle?.compartment_count != null
        ? Number(vehicle.compartment_count)
        : (Array.isArray(vehicle?.compartments) ? vehicle.compartments.length : 0));
  const cap = eff != null ? eff : Number(vehicle?.payload);
  const productOf = (li) => productKey(products, li);

  // fit: 'ok' | 'too-many' | 'reorder' | 'wont-fit' | 'no-spec'
  let fit = 'no-spec';
  let suggestedOrder = null; // line indices in drop order (0 = first drop)
  let doorConfig = null;     // moveable: cumulative door positions to set (count-1)
  let assignment = null;     // rear→front, length count [{ size, lineIndex|null, portion, fill, mixed }]
  let mixed = false;
  let isOverride = false;    // honouring a user-chosen layout rather than auto-pick
  const messages = [];

  // Candidate compartment layouts (rear→front sizes). `forced` (when set) is a
  // user-chosen layout that must be honoured instead of auto-picking.
  let configList = [];
  let forced = null;
  if (!compartmentCount) {
    // no-spec
  } else if (!moveable) {
    const caps = (Array.isArray(vehicle.compartments) ? vehicle.compartments : [])
      .map(c => Number(c?.capacity)).filter(Number.isFinite);
    if (caps.length >= compartmentCount) configList = [{ doors: null, sizes: caps.slice().reverse() }];
  } else if (!Number.isFinite(cap)) {
    // no-spec
  } else {
    const rangeCfgs = moveableRangeConfigs(vehicle, cap);
    if (!rangeCfgs.length) {
      messages.push('No compartment layout satisfies this vehicle’s ranges, half-caps and weight rule for the payload.');
    } else {
      configList = rangeCfgs.map(rc => ({ doors: rc.doors, sizes: rc.sizes }));
      const od = validateRangeDoors(overrideDoors, vehicle, cap);
      if (od) {
        const sorted = overrideDoors.map(Number).filter(Number.isFinite).sort((a, b) => a - b);
        forced = { doors: sorted, sizes: od };
      }
    }
  }

  if (configList.length && n > 0) {
    const tryPack = (sizes, seq) => {
      const a = packUnits(sizes, seq, lineTonnes, productOf, false);
      if (a) return { assign: a, mixed: false };
      const b = packUnits(sizes, seq, lineTonnes, productOf, true);
      if (b) return { assign: b, mixed: true };
      return null;
    };
    const identity = lineTonnes.map((_, i) => i);
    let best = null;
    // The TABLE order is the user's plan: index 0 = top row = first drop = REAR
    // compartment. We never re-sequence a load here — the layout always packs the
    // drops in their current table order. "Auto" optimises the door layout WITHIN
    // that order; only the user may accept a layout that needs a different order.
    // Tracks whether a layout packed geometrically but broke the axle rules, so we
    // can tell the user "re-order the drops" rather than "won't fit".
    let packedButUnbalanced = false;
    if (forced) {
      // Honour the user's chosen layout, packed in the current table order.
      isOverride = true;
      const r0 = tryPack(forced.sizes, identity);
      if (r0 && loadDistributionOk(r0.assign, vehicle)) {
        best = { ...forced, ...r0, seq: identity }; fit = 'ok';
      } else {
        if (r0) packedButUnbalanced = true;
        // The chosen layout can no longer carry the load (in table order / within
        // the axle rules). Surface it but still report the chosen doors.
        doorConfig = forced.doors;
        fit = n > compartmentCount ? 'too-many' : 'wont-fit';
        if (fit === 'wont-fit' && packedButUnbalanced) messages.push(distributionMessage(vehicle));
      }
    } else {
      // Auto-pick the tightest door layout that packs in the CURRENT table order
      // AND keeps the load within the axle rules. No re-sequence.
      for (const cfg of configList) {
        const r = tryPack(cfg.sizes, identity);
        if (!r) continue;
        if (!loadDistributionOk(r.assign, vehicle)) { packedButUnbalanced = true; continue; }
        const us = usedSizeSum(r.assign);
        if (!best || us < best.us) best = { ...cfg, ...r, us, seq: identity };
      }
      if (best) {
        fit = 'ok';
      } else {
        fit = n > compartmentCount ? 'too-many' : 'wont-fit';
        if (n > compartmentCount) messages.push(`${n} drops but only ${compartmentCount} compartment${compartmentCount === 1 ? '' : 's'}.`);
        else if (packedButUnbalanced) messages.push(distributionMessage(vehicle));
      }
    }
    if (best) {
      doorConfig = best.doors;
      assignment = best.assign;
      mixed = best.mixed;
      if (mixed) messages.push('Same-product orders share a compartment.');
    }
  }

  const { payloadUtil, compartmentFill, efficiency } = efficiencyOf(assignment, total, eff);

  return {
    registration: vehicle?.registration,
    payload: vehicle?.payload != null ? Number(vehicle.payload) : null,
    effPayload: eff,
    hasMoffett: !!vehicle?.has_moffett,
    totalTonnes: total,
    overPayload,
    overBy,
    lineCount: n,
    compartmentCount,
    compartmentsUsed: assignment ? assignment.filter(a => a.portion > EPS).length : 0,
    moveable,
    fit,
    mixed,
    isOverride,
    suggestedOrder,
    doorConfig,
    assignment,              // rear→front, length count [{ size, lineIndex|null, portion, fill, mixed }]
    payloadUtil,
    compartmentFill,
    efficiency,
    messages
  };
}

/**
 * Enumerate the possible compartment configurations for a load (moveable bodies
 * = a choice of compartmentCount-1 doors; fixed = the single stored layout).
 *
 * The board's drop order is the user's plan, so each config is reported TWICE:
 *   - IN THE CURRENT SEQUENCE (`fitsCurrentSequence` / `seqEfficiency`) — what you
 *     get by only moving the doors, leaving the rows alone. This is what "Auto"
 *     picks from.
 *   - AT ITS BEST over any candidate order (`fits` / `dropOrder` / `efficiency`).
 *     When `dropOrder` isn't the identity, taking it means re-ordering the table —
 *     offered to the user as a flagged option, never applied automatically.
 * Sorted best-first (in-sequence fits first).
 * @returns {{ doors:number[]|null, sizes:number[], fits:boolean,
 *             fitsCurrentSequence:boolean, seqEfficiency:number|null,
 *             seqMixed:boolean, seqUsedSum:number, dropOrder:number[]|null,
 *             mixed:boolean, usedSum:number, efficiency:number|null }[]}
 *           sizes rear→front.
 */
export function enumerateConfigs(vehicle, lineTonnes, products, maxResults = 60) {
  const n = lineTonnes.length;
  const eff = usablePayload(vehicle);
  const moveable = !!vehicle?.has_moveable_doors;
  const rangesArr = Array.isArray(vehicle?.compartment_ranges) ? vehicle.compartment_ranges : [];
  const compartmentCount = (moveable && rangesArr.length)
    ? rangesArr.length
    : (vehicle?.compartment_count != null
        ? Number(vehicle.compartment_count)
        : (Array.isArray(vehicle?.compartments) ? vehicle.compartments.length : 0));
  if (n === 0 || !compartmentCount) return [];
  const productOf = (li) => productKey(products, li);
  const total = round(sum(lineTonnes));

  const configFor = (doors, sizes) => {
    const identity = lineTonnes.map((_, i) => i);
    // A packing only counts when the load fits the compartments AND obeys the
    // rear-lift weight rule (which only bites at full payload).
    const tryPack = (seq) => {
      const a = packUnits(sizes, seq, lineTonnes, productOf, false);
      const am = a || packUnits(sizes, seq, lineTonnes, productOf, true);
      if (!am || !loadDistributionOk(am, vehicle)) return null;
      return { packed: am, mixed: !a, usedSum: usedSizeSum(am), efficiency: efficiencyOf(am, total, eff).efficiency };
    };

    // 1. In the CURRENT (table) sequence — doors-only, rows untouched.
    const inSeq = tryPack(identity);

    // 2. The best this config can do over any candidate order (identity included),
    //    so we can tell the user a re-sequence would pay off.
    let best = inSeq ? { ...inSeq, seq: identity } : null;
    for (const seq of candidateSeqs(lineTonnes)) {
      if (seq.every((v, i) => v === i)) continue;
      const r = tryPack(seq);
      if (!r) continue;
      if (!best || (r.efficiency ?? -1) > (best.efficiency ?? -1)) best = { ...r, seq };
    }

    return {
      doors,
      sizes,
      // In-sequence view (what Auto may choose):
      fitsCurrentSequence: !!inSeq,
      seqEfficiency: inSeq ? inSeq.efficiency : null,
      seqMixed: inSeq ? inSeq.mixed : false,
      seqUsedSum: inSeq ? inSeq.usedSum : Infinity,
      // Best-over-any-order view (may require a table re-order):
      fits: !!best,
      dropOrder: best ? best.seq : null,
      mixed: best ? best.mixed : false,
      usedSum: best ? best.usedSum : Infinity,
      efficiency: best ? best.efficiency : null
    };
  };

  let out;
  if (!moveable) {
    const caps = (Array.isArray(vehicle?.compartments) ? vehicle.compartments : [])
      .map(c => Number(c?.capacity)).filter(Number.isFinite);
    if (caps.length < compartmentCount) return [];
    out = [configFor(null, caps.slice().reverse())];
  } else {
    const cap = eff != null ? eff : Number(vehicle?.payload);
    if (!Number.isFinite(cap)) return [];
    const rangeCfgs = moveableRangeConfigs(vehicle, cap);
    if (!rangeCfgs.length) return [];
    out = rangeCfgs.map(rc => configFor(rc.doors, rc.sizes));
  }

  // Best first: works in the user's order (most efficient first), then anything
  // that fits after a re-sequence, then tightest (least used compartment size).
  out.sort((a, b) =>
    (b.fitsCurrentSequence - a.fitsCurrentSequence) ||
    ((b.seqEfficiency ?? -1) - (a.seqEfficiency ?? -1)) ||
    (b.fits - a.fits) ||
    ((b.efficiency ?? -1) - (a.efficiency ?? -1)) ||
    (a.usedSum - b.usedSum));
  return out.slice(0, maxResults);
}

const isIdentitySeq = (order) => Array.isArray(order) && order.every((v, i) => v === i);

// The most efficient layout that works WITHOUT touching the user's drop order —
// i.e. only the doors move. This is what the load modal's "Auto" applies.
export function bestInSequenceConfig(configs) {
  const inSeq = (configs || []).filter(c => c.fitsCurrentSequence);
  if (!inSeq.length) return null;
  return inSeq.reduce((a, b) => {
    const ea = a.seqEfficiency ?? -1, eb = b.seqEfficiency ?? -1;
    if (eb !== ea) return eb > ea ? b : a;
    return b.seqUsedSum < a.seqUsedSum ? b : a; // tie → tightest
  });
}

// The most efficient layout available if the drops were RE-ORDERED. Returned only
// when it genuinely beats the best in-sequence option AND needs a different order,
// so the modal can flag it as a choice. Never applied automatically — the drop
// order is the user's plan.
export function bestResequencedConfig(configs, inSeqBest) {
  const cands = (configs || []).filter(c => c.fits && !isIdentitySeq(c.dropOrder));
  if (!cands.length) return null;
  const best = cands.reduce((a, b) => ((b.efficiency ?? -1) > (a.efficiency ?? -1) ? b : a));
  const baseline = inSeqBest ? (inSeqBest.seqEfficiency ?? -1) : -1;
  return (best.efficiency ?? -1) > baseline ? best : null;
}

/**
 * Which compartments each row loads into, as mills write it: "3, 4".
 *
 * Numbered from the cab: compartment 1 is nearest the cab, the highest number
 * is the rear-most. `assignment` from analyzeVehicleLoad runs REAR→FRONT, so
 * entry i is compartment (count − i). A row spanning several compartments gets
 * all of them; same-product rows sharing one each get it.
 *
 * @param {Array} loads   the board's vehicleLoads (analyzeVehicleLoad results
 *                        with their `rows`)
 * @param {Function} keyOf  row → identity (default order_line_id)
 * @returns {Map<any, string>}  row key → "3, 4" (rows with no assignment absent)
 */
/**
 * Row key → its drop number within its load (1 = first drop = top row, the
 * rear-most compartment). Shown as the "D#" badge beside each row's time.
 */
export function dropNumbersByRow(loads, keyOf = (r) => r.order_line_id) {
  const out = new Map();
  for (const load of loads || []) {
    (load.rows || []).forEach((r, i) => out.set(keyOf(r), i + 1));
  }
  return out;
}

/** "06:00", or "06:00 / 07:00" for a run collecting at several times. */
export const loadTimeLabel = (load) => {
  const times = (load?.times && load.times.length ? load.times : [load?.time]).filter(Boolean);
  return times.map(t => String(t).slice(0, 5)).join(' / ');
};

export function compartmentsByRow(loads, keyOf = (r) => r.order_line_id) {
  const out = new Map();
  for (const load of loads || []) {
    const assign = load.assignment;
    if (!Array.isArray(assign) || !assign.length || !Array.isArray(load.rows)) continue;
    const perLine = new Map();
    assign.forEach((a, i) => {
      if (!(a.portion > EPS)) return;
      const number = assign.length - i;
      const lines = Array.isArray(a.lines) && a.lines.length ? a.lines : (a.lineIndex != null ? [a.lineIndex] : []);
      for (const li of lines) {
        if (!perLine.has(li)) perLine.set(li, new Set());
        perLine.get(li).add(number);
      }
    });
    for (const [li, numbers] of perLine) {
      const row = load.rows[li];
      if (!row) continue;
      out.set(keyOf(row), [...numbers].sort((x, y) => x - y).join(', '));
    }
  }
  return out;
}
